import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';
import { formatWhatsAppLink } from '../utils/whatsapp';
import { resolveImageUrl } from '../utils/imageUrl';

const categoryImages = {
  'bombas-hidraulicas': 'https://static.prod-images.emergentagent.com/jobs/4f92bd5b-bc39-4960-a863-acbe67095213/images/2fe93fb512d0b7a8be44406088b14eb40cb6df8364ddc09708880f019b0079e1.png',
  'motores-hidraulicos': 'https://static.prod-images.emergentagent.com/jobs/4f92bd5b-bc39-4960-a863-acbe67095213/images/2944b4374ed29dff220af55c8947bcfa4717beddb8ebd5f99fefd1c38a580f13.png',
  'comandos-hidraulicos': 'https://static.prod-images.emergentagent.com/jobs/4f92bd5b-bc39-4960-a863-acbe67095213/images/1e7a0bcda1cb0779d588d1cc6184055685280c479269bfcebc1215ca99bd327f.png',
  'valvulas-hidraulicas': 'https://static.prod-images.emergentagent.com/jobs/4f92bd5b-bc39-4960-a863-acbe67095213/images/6867fba57dda82811d288dbf4cf35120d6224172113be415e8e2f92d923213f1.png',
  'mangueiras-e-conexoes': 'https://static.prod-images.emergentagent.com/jobs/4f92bd5b-bc39-4960-a863-acbe67095213/images/cd429a243794bc5ca0f81e55c28f18685ba0b88086fde3b52865e0a81bd47ec8.png',
};

const PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNFNUU3RUIiLz48dGV4dCB4PSIyMDAiIHk9IjIwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Q0EzQUYiPlNlbSBJbWFnZW08L3RleHQ+PC9zdmc+';

const Categories = () => {
  const navigate = useNavigate();
  const { categories } = useSiteData();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [categories]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-cat-card]')?.offsetWidth || 250;
    el.scrollBy({ left: direction * (cardWidth + 24), behavior: 'smooth' });
  };

  return (
    <section data-testid="categories-section" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#055509] mb-3">
            Categorias de Produtos
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Componentes hidráulicos de alta performance para aplicações industriais e agrícolas
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative group/carousel">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              data-testid="categories-scroll-left"
              onClick={() => scroll(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white/95 shadow-lg border border-gray-200 rounded-full flex items-center justify-center text-[#055509] hover:bg-[#055509] hover:text-white transition-all duration-200 -ml-4 backdrop-blur-sm"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              data-testid="categories-scroll-right"
              onClick={() => scroll(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white/95 shadow-lg border border-gray-200 rounded-full flex items-center justify-center text-[#055509] hover:bg-[#055509] hover:text-white transition-all duration-200 -mr-4 backdrop-blur-sm"
            >
              <ChevronRight size={22} />
            </button>
          )}

          {/* Scrollable Track */}
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scroll-smooth pb-4 px-1 snap-x snap-mandatory categories-scroll"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            <style>{`
              .categories-scroll::-webkit-scrollbar { display: none; }
            `}</style>
            {categories.map((category) => {
              const fallbackImg = categoryImages[category.slug] || categoryImages['bombas-hidraulicas'];
              const dbImage = category.imageUrl;
              const isExternal = dbImage && dbImage.startsWith('http');
              const catImage = isExternal ? dbImage : fallbackImg;
              return (
                <div
                  key={category.id || category.slug}
                  data-testid={`category-card-${category.slug}`}
                  data-cat-card="true"
                  className="group cursor-pointer flex-shrink-0 snap-start"
                  style={{ width: 'calc((100% - 80px) / 5)', minWidth: '180px' }}
                  onClick={() => navigate(`/categorias/${category.slug}`)}
                >
                  <div className="relative bg-gray-50 rounded-xl overflow-hidden border-2 border-gray-100 hover:border-[#FFB025] transition-all duration-300 hover:shadow-xl h-full">
                    <div className="aspect-square p-4 flex items-center justify-center overflow-hidden">
                      <img
                        src={catImage}
                        alt={category.name}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => { e.target.onerror = null; e.target.src = fallbackImg || PLACEHOLDER; }}
                      />
                    </div>
                    <div className="bg-[#055509] px-3 py-3 text-center group-hover:bg-[#0a7a0f] transition-colors">
                      <h3 className="text-white font-semibold text-sm leading-tight">
                        {category.name}
                      </h3>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fade edges when scrollable */}
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none z-[5]" />
          )}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-[5]" />
          )}
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <p className="text-gray-600 mb-3 text-sm">Não encontrou o que procura?</p>
          <button
            onClick={() => window.open(formatWhatsAppLink(), '_blank')}
            className="text-[#055509] font-semibold hover:text-[#FFB025] transition-colors inline-flex items-center gap-2 group text-sm"
          >
            <MessageCircle size={18} />
            Fale com nosso time técnico
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Categories;
